import React, { FC, useState, useEffect, ChangeEvent } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Box, Typography, Chip, Divider, Select, TextField, MenuItem, SelectChangeEvent, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Steps, OrderStep } from 'models/workflow-step';
import { Sample, SampleType } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { User } from 'models/user';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Organization } from 'models/organization';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { millingDeadlineDate } from 'utils/milling-deadline-date';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { orderStepVar } from 'apollo/reactive-variables/order';

type WorkFlowStepProps = {
    sample: Sample;
    handleOrderStep: (orderStep: OrderStep) => void;
};
const WorkFlowStep: FC<WorkFlowStepProps> = ({ sample, handleOrderStep }: WorkFlowStepProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'workflow', 'milling-jobs']);
    const currentOrg = currentOrgVar();
    const currentUser = currentUserVar();
    const [steps, setSteps] = useState<Steps[]>(currentOrg.workFlow?.steps || []);
    const [currentStep, setCurrentStep] = useState<Steps>({} as Steps);
    const [userId, setUserId] = useState(currentUser._id);
    const [comment, setComment] = useState('');
    const [deadline, setDeadline] = useState<Date | null>(null);
    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );
    const { showBoundary } = useDenthubErrorBoundary();

    // Both read straight from the query result, never copied into state from onCompleted (DD-3654).
    // Answered from the cache, Apollo runs onCompleted in a microtask even for a render React
    // throws away. When the delivery step suspended on a translation namespace, that setState
    // re-rendered the step inside the same click, the step suspended again, and the tab froze.
    const { data: techniciansData } = useQuery<{ technicians: User[] }>(getTechniciansForUserListTemplate, {
        variables: {
            labId: (currentUser.organization as Organization)?._id,
            filters: { active: true },
            limit: 0,
            offset: 0,
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const users = techniciansData?.technicians ?? [];

    const { data: labSettingsData } = useQuery<{ currentLab: Organization }>(labSettingsTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });
    const labAvailability = labSettingsData?.currentLab;

    useEffect(() => {
        if (!currentOrg.workFlow) {
            return;
        }
        setSteps([...currentOrg.workFlow.steps]);
        if (sample) {
            const defaultStep = currentOrg.workFlow?.steps.find((step) => {
                if (sample.type === SampleType.NO_SAMPLE) {
                    return currentOrg.workFlow?.defaultNoSample === step._id;
                }
                if (sample.type === SampleType.DIGITAL) {
                    return currentOrg.workFlow?.defaultDigitalSample === step._id;
                }
                if (sample.type === SampleType.PHYSICAL) {
                    return currentOrg.workFlow?.defaultPhysicalSample === step._id;
                }
                return false;
            });
            const step = defaultStep || { name: 'NO_STEP' };
            handleChangeStep(step as Steps, comment, userId, deadline);
            setCurrentStep(step as Steps);
        }
    }, [sample]);

    useEffect(() => {
        if (
            isLabMillingEnabled &&
            currentStep.useMillingDeadline &&
            currentStep.millingDeadlineDays &&
            labAvailability
        ) {
            const deadlineDate = millingDeadlineDate(currentStep.millingDeadlineDays as number, labAvailability);
            setDeadline(deadlineDate);
        } else {
            setDeadline(null);
        }
    }, [currentStep]);

    const handleChangeStep = (currentStep: Steps, comment: string, userId: string, deadline: Date | null) => {
        const defaultExtUsers = currentStep.defaultExtUsers?.map((user) => (user as User)._id);
        const orderCurrentStep = {
            stepId: currentStep._id,
            name: currentStep.name,
            description: currentStep.description,
            comment: comment,
            maxHourInStep: currentStep.maxHourInStep,
            userId: userId,
            extUsers: defaultExtUsers,
            millingDeadline: deadline,
            useMillingDeadline: currentStep.useMillingDeadline,
        };
        let orderStep = orderStepVar();
        orderStep = Object.assign(orderStep || {}, orderCurrentStep);
        orderStepVar(orderStep);
        handleOrderStep(orderStep);
    };

    const handleStepClick = (step: Steps) => () => {
        handleChangeStep(step, comment, userId, deadline);
        setCurrentStep(step);
    };

    const onCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        handleChangeStep(currentStep, value, userId, deadline);
        setComment(value);
    };

    const handleUserChange = (event: SelectChangeEvent) => {
        handleChangeStep(currentStep, comment, event.target.value, deadline);
        setUserId(event.target.value);
    };

    const handleDateChange = (date: Date | null) => {
        handleChangeStep(currentStep, comment, userId, date as Date);
        setDeadline(date as Date);
    };
    return (
        <Paper variant="outlined" sx={{ height: '100%' }}>
            <Box p={1}>
                <Typography variant="h4">
                    <Box>{translate('workflow:WORKFLOW')}</Box>
                </Typography>
                <Box my={1}>
                    <Divider />
                </Box>
                <Typography variant="subtitle2" mb={1}>
                    {translate('orders:SELECT_NEW_WORKFLOW_STEP')}
                </Typography>
                <Box mb={2} display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
                    <Chip
                        size="medium"
                        clickable
                        label={translate('workflow:NO_STEP')}
                        color={!currentStep?._id ? 'primary' : undefined}
                        onClick={handleStepClick({ name: 'NO_STEP' } as Steps)}
                        data-test="workflow-step.no-step-chip"
                    />
                    {steps?.map((step, index) => (
                        <Chip
                            key={index}
                            size="medium"
                            clickable
                            color={currentStep?._id === step._id ? 'primary' : undefined}
                            label={`${index + 1} - ${step.name}`}
                            onClick={handleStepClick(step)}
                            data-test={`workflow-step.chip-${index}`}
                        />
                    ))}
                </Box>

                <Box mb={2}>
                    <Typography variant="subtitle2">{translate('USER')}</Typography>
                    <Select
                        fullWidth
                        value={userId}
                        onChange={handleUserChange}
                        renderValue={(value) => {
                            const item = users.find(({ _id: v }) => v === value);
                            return item ? `${item.name.firstName} ${item.name.lastName}` : value;
                        }}
                        data-test="workflow-step.user-select"
                    >
                        {users.map((user, index) => (
                            <MenuItem value={user._id} key={index} data-test={`workflow-step.user-menuitem-${index}`}>
                                {`${user.name.firstName} ${user.name.lastName}`}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        onChange={onCommentChange}
                        label={translate('workflow:STEP_COMMENTS')}
                        data-test="workflow-step.comments-input"
                    />
                </Box>
                {isLabMillingEnabled && currentStep.useMillingDeadline ? (
                    <Box mb={2}>
                        <Typography variant="subtitle2">{translate('milling-jobs:MILLING_DEADLINE')}</Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                            <div>
                                <DatePicker
                                    format={getDatePickerFormat()}
                                    minDate={new Date()}
                                    value={deadline}
                                    onChange={handleDateChange}
                                    slotProps={{
                                        textField: { inputProps: { 'data-test': 'workflow-step.deadline-datepicker' } },
                                    }}
                                />
                            </div>
                        </LocalizationProvider>
                    </Box>
                ) : undefined}
            </Box>
        </Paper>
    );
};

export default WorkFlowStep;
